import React, { useEffect, useState } from 'react';
import { slotPolicyApi } from '../api/SlotPolicyApi';
import { standardAreaApi } from "@/features/policy/standardAreaManagement/api/StandardAreaApi";
import {
    DAY_LABELS,
    DEFAULT_FORM,
    DEFAULT_SLOT_ITEM,
    SaveSlotPolicyRequest,
    SlotDayItemForm,
    SlotInfoCodeDto,
    SlotItemForm,
    SlotPolicyDto,
    SlotPolicyForm,
} from '../types/slotPolicy.types';
import '../styles/SlotManagement.css';

interface SlotPolicyModalProps {
    isOpen: boolean;
    editTarget: SlotPolicyDto | null;
    hasGlobalPolicy?: boolean;
    onSave: (req: SaveSlotPolicyRequest) => Promise<void>;
    onClose: () => void;
}

function parseMinutes(time: string): number | null {
    if (!time || !/^\d{2}:\d{2}$/.test(time)) return null;
    const [h, m] = time.split(':').map(Number);
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return h * 60 + m;
}

function slotIntervals(startMin: number, endMin: number): [number, number][] {
    if (endMin >= startMin) return [[startMin, endMin + 1]];
    return [[startMin, 1440], [0, endMin + 1]];
}

function slotsHaveOverlap(slots: { days: SlotDayItemForm[] }[]): boolean {
    for (let dow = 1; dow <= 7; dow++) {
        type Seg = [number, number, number];
        const segs: Seg[] = [];
        for (let i = 0; i < slots.length; i++) {
            const day = slots[i].days.find(d => d.dayOfWeek === dow);
            if (!day) continue;
            const s = parseMinutes(day.startTime);
            const e = parseMinutes(day.endTime);
            if (s === null || e === null) continue;
            for (const [is, ie] of slotIntervals(s, e)) segs.push([is, ie, i]);
        }
        for (let i = 0; i < segs.length; i++) {
            for (let j = i + 1; j < segs.length; j++) {
                if (segs[i][2] === segs[j][2]) continue;
                if (segs[i][0] < segs[j][1] && segs[j][0] < segs[i][1]) return true;
            }
        }
    }
    return false;
}

// 배민 영업일 기준 06:00~05:59, 전 시간대 선택 가능
// 순서: 06, 07, ..., 23, 00, 01, 02, 03, 04, 05
const VALID_HOURS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0, 1, 2, 3, 4, 5];

const pad2 = (n: number) => String(n).padStart(2, '0');

const getHour = (time: string): string => {
    if (!time || !/^\d{2}:\d{2}$/.test(time)) return '';
    return String(parseInt(time.slice(0, 2), 10));
};

const toDateInput = (v: string) =>
    v.length === 8 ? `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}` : '';
const fromDateInput = (v: string) => v.replace(/-/g, '');

// 신규 정책 등록 시 기본 슬롯 설정 (평일=1~5, 주말=6~7)
const DEFAULT_SLOTS: SlotItemForm[] = [
    {
        seqNo: null,
        slotName: '아침',
        days: [1, 2, 3, 4, 5, 6, 7].map(dow => ({ dayOfWeek: dow, startTime: '06:00', endTime: '08:59', targetCnt: '' })),
    },
    {
        seqNo: null,
        slotName: '아침점심피크',
        days: [1, 2, 3, 4, 5, 6, 7].map(dow => ({
            dayOfWeek: dow,
            startTime: '09:00',
            endTime: dow <= 5 ? '12:59' : '13:59',
            targetCnt: '',
        })),
    },
    {
        seqNo: null,
        slotName: '오후논피크',
        days: [1, 2, 3, 4, 5, 6, 7].map(dow => ({
            dayOfWeek: dow,
            startTime: dow <= 5 ? '13:00' : '14:00',
            endTime: '16:59',
            targetCnt: '',
        })),
    },
    {
        seqNo: null,
        slotName: '저녁피크',
        days: [1, 2, 3, 4, 5, 6, 7].map(dow => ({ dayOfWeek: dow, startTime: '17:00', endTime: '19:59', targetCnt: '' })),
    },
    {
        seqNo: null,
        slotName: '심야논피크',
        days: [1, 2, 3, 4, 5, 6, 7].map(dow => ({ dayOfWeek: dow, startTime: '20:00', endTime: '23:59', targetCnt: '' })),
    },
    {
        seqNo: null,
        slotName: '심야',
        days: [1, 2, 3, 4, 5, 6, 7].map(dow => ({ dayOfWeek: dow, startTime: '00:00', endTime: '05:59', targetCnt: '0' })),
    },
];

// SLOTINFO 공통코드(SLOT1~SLOT4)의 요일별 건수를 DEFAULT_SLOTS에 적용
// SLOT1→index 1(아침점심피크), SLOT2→index 2(오후논피크), SLOT3→index 3(저녁피크), SLOT4→index 4(심야논피크)
const SLOTINFO_IDX: Record<string, number> = { SLOT1: 1, SLOT2: 2, SLOT3: 3, SLOT4: 4 };
const VAL_KEYS = ['val1', 'val2', 'val3', 'val4', 'val5', 'val6', 'val7'] as const;

function applySlotInfoTargets(codes: SlotInfoCodeDto[]): SlotItemForm[] {
    const slots = DEFAULT_SLOTS.map(s => ({
        ...s,
        days: s.days.map(d => ({ ...d })),
    }));
    for (const code of codes) {
        const idx = SLOTINFO_IDX[code.comCode];
        if (idx === undefined) continue;
        slots[idx] = {
            ...slots[idx],
            days: slots[idx].days.map((d, i) => ({
                ...d,
                targetCnt: code[VAL_KEYS[i]] ?? '',
            })),
        };
    }
    return slots;
}

const SlotPolicyModal: React.FC<SlotPolicyModalProps> = ({ isOpen, editTarget, hasGlobalPolicy, onSave, onClose }) => {
    const [form, setForm] = useState<SlotPolicyForm>({ ...DEFAULT_FORM, slots: [{ ...DEFAULT_SLOT_ITEM }] });
    const [saving, setSaving] = useState(false);
    const [availableAreas, setAvailableAreas] = useState<string[]>([]);
    const [areaSearch, setAreaSearch] = useState('');

    useEffect(() => {
        if (!isOpen) return;

        standardAreaApi.getStandardAreas()
            .then(res => setAvailableAreas((res.areas ?? []).map(a => a.areaName)))
            .catch(() => setAvailableAreas([]));
        setAreaSearch('');

        if (editTarget) {
            setForm({
                seqNo: editTarget.seqNo,
                policyName: editTarget.policyName ?? '',
                applyStartDate: editTarget.applyStartDate ?? '',
                requiredSlotCnt: editTarget.requiredSlotCnt != null ? String(editTarget.requiredSlotCnt) : '',
                acceptanceRate: editTarget.acceptanceRate != null ? String(editTarget.acceptanceRate) : '',
                selectedStandardAreas: editTarget.standardAreas ?? [],
                useYn: editTarget.useYn ?? 'Y',
                isDefault: !(editTarget.standardAreas?.length),
                slots: editTarget.slots?.length
                    ? editTarget.slots.map(s => ({
                        seqNo: s.seqNo,
                        slotName: s.slotName ?? '',
                        days: (s.days ?? []).map(d => ({
                            dayOfWeek: d.dayOfWeek,
                            startTime: d.startTime ?? '',
                            endTime: d.endTime ?? '',
                            targetCnt: d.targetCnt ?? '',
                        })),
                    }))
                    : [{ ...DEFAULT_SLOT_ITEM }],
            });
        } else {
            slotPolicyApi.getSlotInfoDefaults()
                .then(res => {
                    setForm({ ...DEFAULT_FORM, slots: applySlotInfoTargets(res.codes ?? []) });
                })
                .catch(() => {
                    setForm({ ...DEFAULT_FORM, slots: DEFAULT_SLOTS });
                });
        }
    }, [isOpen, editTarget]);

    if (!isOpen) return null;

    const setField = (key: keyof SlotPolicyForm, value: string) =>
        setForm(prev => ({ ...prev, [key]: value }));

    const setSlotName = (idx: number, value: string) =>
        setForm(prev => {
            const slots = [...prev.slots];
            slots[idx] = { ...slots[idx], slotName: value };
            return { ...prev, slots };
        });

    const setDayField = (slotIdx: number, dayIdx: number, key: keyof SlotDayItemForm, value: string) =>
        setForm(prev => {
            const slots = [...prev.slots];
            const days = [...slots[slotIdx].days];
            days[dayIdx] = { ...days[dayIdx], [key]: value };
            slots[slotIdx] = { ...slots[slotIdx], days };
            return { ...prev, slots };
        });

    const handleDefaultToggle = async () => {
        if (!form.isDefault) {
            if (hasGlobalPolicy && !editTarget) {
                alert('전체 협력사 적용(기본) 정책은 하나만 등록할 수 있습니다.');
                return;
            }
            try {
                const res = await slotPolicyApi.getSlotInfoDefaults();
                setForm(prev => ({
                    ...prev,
                    isDefault: true,
                    selectedStandardAreas: [],
                    slots:applySlotInfoTargets(res.codes ?? []),
                }));
            } catch {
                setForm(prev => ({
                    ...prev,
                    isDefault: true,
                    selectedStandardAreas: [],
                    slots: DEFAULT_SLOTS,
                }));
            }
        } else {
            setForm(prev => ({ ...prev, isDefault: false }));
        }
    };

    const addSlot = () =>
        setForm(prev => ({ ...prev, slots: [...prev.slots, { ...DEFAULT_SLOT_ITEM }] }));

    const removeSlot = (index: number) =>
        setForm(prev => ({ ...prev, slots: prev.slots.filter((_, i) => i !== index) }));

    const handleSave = async () => {
        if (!form.policyName.trim()) {
            alert('그룹명은 필수입니다.');
            return;
        }
        if (form.slots.some(s => !s.slotName.trim())) {
            alert('슬롯명은 필수입니다.');
            return;
        }
        if (slotsHaveOverlap(form.slots)) {
            alert('슬롯 시간대가 겹칩니다. 시간을 다시 확인해주세요.');
            return;
        }

        const req: SaveSlotPolicyRequest = {
            seqNo: form.seqNo,
            policyName: form.policyName.trim(),
            applyStartDate: form.applyStartDate.trim(),
            requiredSlotCnt: form.requiredSlotCnt !== '' ? Number(form.requiredSlotCnt) : null,
            acceptanceRate: form.acceptanceRate !== '' ? Number(form.acceptanceRate) : null,
            standardAreas: form.selectedStandardAreas,
            slots: form.slots.map((s, i) => ({
                seqNo: s.seqNo,
                slotOrder: i + 1,
                slotName: s.slotName.trim(),
                days: s.days.map(d => ({
                    dayOfWeek: d.dayOfWeek,
                    startTime: d.startTime,
                    endTime: d.endTime,
                    targetCnt: d.targetCnt,
                })),
            })),
            userId: '',
        };

        setSaving(true);
        try {
            await onSave(req);
        } finally {
            setSaving(false);
        }
    };

    const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="slot-modal-overlay" onClick={handleBackdrop}>
            <div className="slot-modal" onClick={e => e.stopPropagation()}>
                {/* 헤더 */}
                <div className="slot-modal-header">
                    <h2 className="slot-modal-title">
                        {editTarget ? '슬롯 정책 수정' : '슬롯 정책 등록'}
                    </h2>
                </div>

                {/* 본문 */}
                <div className="slot-modal-body">
                    {/* 기본 정보 */}
                    <div className="slot-form-grid">
                        <div className="slot-form-group">
                            <label className="slot-form-label required">그룹명</label>
                            <input
                                className="slot-form-input"
                                type="text"
                                placeholder="예) 안성A"
                                value={form.policyName}
                                onChange={e => setField('policyName', e.target.value)}
                            />
                        </div>

                        <div className="slot-form-group">
                            <label className="slot-form-label">적용 시작일</label>
                            <input
                                className="slot-form-input"
                                type="date"
                                value={toDateInput(form.applyStartDate)}
                                onChange={e => setField('applyStartDate', fromDateInput(e.target.value))}
                            />
                            {!form.applyStartDate && (
                                <span className="slot-form-hint">비워두면 오늘부터 즉시 적용됩니다</span>
                            )}
                        </div>

                        {/* 필수 달성 슬롯 수 - 당장 불필요하여 숨김
                        <div className="slot-form-group">
                            <label className="slot-form-label">필수 달성 슬롯 수</label>
                            <input
                                className="slot-form-input"
                                type="number"
                                min={1}
                                max={4}
                                placeholder="예) 3"
                                value={form.requiredSlotCnt}
                                onChange={e => setField('requiredSlotCnt', e.target.value)}
                            />
                        </div>

                        <div className="slot-form-group">
                            <label className="slot-form-label">수락률 (%)</label>
                            <input
                                className="slot-form-input"
                                type="number"
                                min={0}
                                max={100}
                                placeholder="예) 90"
                                value={form.acceptanceRate}
                                onChange={e => setField('acceptanceRate', e.target.value)}
                            />
                        </div>
                        */}

                        <div className="slot-form-group">
                            <label className="slot-form-label">사용 여부</label>
                            <select
                                className="slot-form-select"
                                value={form.useYn}
                                onChange={e => setField('useYn', e.target.value)}
                            >
                                <option value="Y">Y (사용)</option>
                                <option value="N">N (미사용)</option>
                            </select>
                        </div>

                        <div className="slot-form-group full-width">
                            <label className="slot-form-label">
                                표준권역
                                <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 6 }}>
                                    {form.isDefault ? '기본' : `(${form.selectedStandardAreas.length}개 선택됨)`}
                                </span>
                            </label>
                            <label className="slot-area-item" style={{ marginBottom: 6 }}>
                                <input
                                    type="checkbox"
                                    checked={form.isDefault}
                                    onChange={handleDefaultToggle}
                                />
                                기본 (모든 협력사에 적용되는 기본 정책)
                            </label>
                            <input
                                className="slot-form-input"
                                type="text"
                                placeholder="권역명 검색"
                                value={areaSearch}
                                onChange={e => setAreaSearch(e.target.value)}
                                style={{ marginBottom: 6 }}
                                disabled={form.isDefault}
                            />
                            <div className="slot-area-list">
                                {availableAreas.length === 0 ? (
                                    <div className="slot-area-empty">
                                        {areaSearch
                                            ? '검색 결과가 없습니다.'
                                            : '등록된 표준권역이 없습니다. 표준권역 관리에서 먼저 등록해주세요.'}
                                    </div>
                                ) : (
                                    availableAreas
                                        .filter(a => a.toLowerCase().includes(areaSearch.toLowerCase()))
                                        .map(areaName => (
                                            <label key={areaName} className="slot-area-item" style={form.isDefault ? { opacity: 0.4, pointerEvents: 'none' } : undefined}>
                                                <input
                                                    type="checkbox"
                                                    checked={form.selectedStandardAreas.includes(areaName)}
                                                    disabled={form.isDefault}
                                                    onChange={() =>
                                                        setForm(prev => ({
                                                            ...prev,
                                                            selectedStandardAreas: prev.selectedStandardAreas.includes(areaName)
                                                                ? prev.selectedStandardAreas.filter(a => a !== areaName)
                                                                : [...prev.selectedStandardAreas, areaName],
                                                        }))
                                                    }
                                                />
                                                {areaName}
                                            </label>
                                        ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 슬롯 목록 */}
                    <div className="slot-section-title">
                        <span>슬롯 설정</span>
                        <button className="btn-add-slot" type="button" onClick={addSlot}>
                            + 슬롯 추가
                        </button>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table className="slot-rows-table">
                            <thead>
                            <tr>
                                <th style={{ minWidth: 110 }} rowSpan={2}>슬롯명</th>
                                {DAY_LABELS.map(d => (
                                    <th key={d} colSpan={3} style={{ minWidth: 180, textAlign: 'center' }}>{d}</th>
                                ))}
                                <th style={{ minWidth: 44 }} rowSpan={2}></th>
                            </tr>
                            <tr>
                                {DAY_LABELS.map(d => (
                                    <React.Fragment key={d}>
                                        <th style={{ minWidth: 56, fontWeight: 400, fontSize: 11 }}>시작</th>
                                        <th style={{ minWidth: 56, fontWeight: 400, fontSize: 11 }}>종료</th>
                                        <th style={{ minWidth: 50, fontWeight: 400, fontSize: 11 }}>건수</th>
                                    </React.Fragment>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {form.slots.map((slot, slotIdx) => (
                                <tr key={slotIdx}>
                                    <td>
                                        <input
                                            className="slot-cell-input name-input"
                                            type="text"
                                            placeholder="슬롯명"
                                            value={slot.slotName}
                                            onChange={e => setSlotName(slotIdx, e.target.value)}
                                        />
                                    </td>
                                    {slot.days.map((day, dayIdx) => (
                                        <React.Fragment key={day.dayOfWeek}>
                                            <td>
                                                <select
                                                    className="slot-cell-input"
                                                    value={getHour(day.startTime)}
                                                    onChange={e =>
                                                        setDayField(slotIdx, dayIdx, 'startTime', `${pad2(Number(e.target.value))}:00`)
                                                    }
                                                >
                                                    <option value="">--</option>
                                                    {VALID_HOURS.map(h => (
                                                        <option key={h} value={h}>{pad2(h)}:00</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <select
                                                    className="slot-cell-input"
                                                    value={getHour(day.endTime)}
                                                    onChange={e =>
                                                        setDayField(slotIdx, dayIdx, 'endTime', `${pad2(Number(e.target.value))}:59`)
                                                    }
                                                >
                                                    <option value="">--</option>
                                                    {VALID_HOURS.map(h => (
                                                        <option key={h} value={h}>{pad2(h)}:59</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <input
                                                    className="slot-cell-input"
                                                    type="number"
                                                    min={0}
                                                    placeholder="0"
                                                    value={day.targetCnt}
                                                    onChange={e => setDayField(slotIdx, dayIdx, 'targetCnt', e.target.value)}
                                                />
                                            </td>
                                        </React.Fragment>
                                    ))}
                                    <td style={{ textAlign: 'center' }}>
                                        <button
                                            className="btn-remove-slot"
                                            type="button"
                                            onClick={() => removeSlot(slotIdx)}
                                            disabled={form.slots.length === 1}
                                        >
                                            ✕
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 푸터 */}
                <div className="slot-modal-footer">
                    <button className="btn-modal-cancel" type="button" onClick={onClose}>
                        취소
                    </button>
                    <button
                        className="btn-modal-save"
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? '저장 중...' : '저장'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SlotPolicyModal;
